import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import reverseGeocode from "../services/reverseGeocode";
import canonicalLocation from "../services/canonicalLocation";
import getBrowserPosition from "../services/getBrowserPosition";
import getCities from "../../../features/profile/services/getCities";

const LocationContext = createContext(null);

const LOCATION_STORAGE_KEY = "baytbuddy_detected_location";
const LOCATION_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

const FALLBACK_CITY_NAME = "Riyadh";
const FALLBACK_CITY_NAME_AR = "الرياض";

const SUPPORTED_COUNTRY_KEYWORDS = [
  "saudi arabia",
  "kingdom of saudi arabia",
  "ksa",
  "saudi",
  "المملكة العربية السعودية",
  "السعودية",
];

function getStoredLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function setStoredLocation(location) {
  try {
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));
  } catch (error) {
    // ignore storage failure
  }
}

function removeStoredLocation() {
  try {
    localStorage.removeItem(LOCATION_STORAGE_KEY);
  } catch (error) {
    // ignore storage failure
  }
}

function normalizeText(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/[,_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildSearchableLocationText(apiData = {}) {
  return [
    apiData?.country,
    apiData?.state,
    apiData?.city,
    apiData?.area,
    apiData?.formatted_address,
  ]
    .filter(Boolean)
    .map((item) => normalizeText(item))
    .join(" | ");
}

function isLocationStale(location) {
  if (!location?.detectedAt) {
    return true;
  }

  const detectedAt = Number(location.detectedAt);

  if (!Number.isFinite(detectedAt)) {
    return true;
  }

  return Date.now() - detectedAt > LOCATION_REFRESH_INTERVAL_MS;
}

function isSaudiArabiaLocation(apiData = {}) {
  const searchableText = buildSearchableLocationText(apiData);

  return SUPPORTED_COUNTRY_KEYWORDS.some((keyword) =>
    searchableText.includes(normalizeText(keyword))
  );
}

function findMatchedCityFromMasterCities(apiData = {}, masterCities = []) {
  if (!Array.isArray(masterCities) || masterCities.length === 0) {
    return null;
  }

  const searchableText = buildSearchableLocationText(apiData);

  let bestMatch = null;
  let bestScore = 0;

  for (const city of masterCities) {
    const cityName = normalizeText(city?.name || "");
    const cityNameAr = normalizeText(city?.name_ar || "");

    let score = 0;

    if (cityName && searchableText.includes(cityName)) {
      score += cityName.length + 10;
    }

    if (cityNameAr && searchableText.includes(cityNameAr)) {
      score += cityNameAr.length + 10;
    }

    if (cityName && normalizeText(apiData?.city || "") === cityName) {
      score += 25;
    }

    if (cityNameAr && normalizeText(apiData?.city || "") === cityNameAr) {
      score += 25;
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = city;
    }
  }

  return bestMatch;
}

function findFallbackCityFromMasterCities(masterCities = []) {
  if (!Array.isArray(masterCities) || masterCities.length === 0) {
    return null;
  }

  const riyadhCity = masterCities.find((city) => {
    const cityName = normalizeText(city?.name || "");
    const cityNameAr = normalizeText(city?.name_ar || "");

    return (
      cityName === normalizeText(FALLBACK_CITY_NAME) ||
      cityNameAr === normalizeText(FALLBACK_CITY_NAME_AR)
    );
  });

  return riyadhCity || null;
}

function buildNormalizedLocation(apiData = {}, matchedCity = null, options = {}) {
  return {
    cityId: matchedCity?.id || "",
    city: matchedCity?.name || "",
    cityAr: matchedCity?.name_ar || "",
    stateId: matchedCity?.state_id || "",
    country: apiData?.country || "",
    sourceState: apiData?.state || "",
    sourceArea: apiData?.area || "",
    sourceDistrict: apiData?.city || "",
    sourceFormattedAddress: apiData?.formatted_address || "",
    detectedAt: Date.now(),
    isResolvedFromMasterCity: Boolean(options?.isResolvedFromMasterCity),
    isFallbackLocation: Boolean(options?.isFallbackLocation),
    isOutsideSupportedRegion: Boolean(options?.isOutsideSupportedRegion),
    fallbackReason: options?.fallbackReason || "",
  };
}

export function LocationProvider({ children }) {
  const [location, setLocation] = useState(() => getStoredLocation());
  const [cities, setCities] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationError, setLocationError] = useState("");
  const automaticDetectionAttempted = useRef(false);
  const locationRevision = useRef(0);

  const updateLocation = useCallback((nextLocation) => {
    const canonical = canonicalLocation(nextLocation, cities);
    locationRevision.current += 1;
    setLocation(canonical);
    setStoredLocation(canonical);
    setIsDetectingLocation(false);
  }, [cities]);

  const clearLocation = useCallback(() => {
    locationRevision.current += 1;
    setLocation(null);
    removeStoredLocation();
  }, []);

  const loadCities = useCallback(async () => {
    setIsLoadingCities(true);

    try {
      const response = await getCities();
      const cityList = Array.isArray(response?.data) ? response.data : [];
      setCities(cityList);
      return cityList;
    } catch (error) {
      setCities([]);
      throw error;
    } finally {
      setIsLoadingCities(false);
    }
  }, []);

  const fetchLocationFromCoordinates = useCallback(
    async (latitude, longitude, citiesListParam = null) => {
      const requestRevision = locationRevision.current;
      setIsDetectingLocation(true);
      setLocationError("");

      try {
        const resolvedCities =
          Array.isArray(citiesListParam) && citiesListParam.length > 0
            ? citiesListParam
            : cities.length > 0
              ? cities
              : await loadCities();

        const response = await reverseGeocode({
          lat: latitude,
          lng: longitude,
        });

        const apiData = response?.data || {};
        const fallbackCity = findFallbackCityFromMasterCities(resolvedCities);
        const isInsideSaudiArabia = isSaudiArabiaLocation(apiData);

        let matchedCity = null;
        let normalizedLocation = null;

        if (isInsideSaudiArabia) {
          matchedCity = findMatchedCityFromMasterCities(apiData, resolvedCities);

          if (matchedCity) {
            normalizedLocation = buildNormalizedLocation(apiData, matchedCity, {
              isResolvedFromMasterCity: true,
              isFallbackLocation: false,
              isOutsideSupportedRegion: false,
              fallbackReason: "",
            });
          } else {
            normalizedLocation = buildNormalizedLocation(apiData, fallbackCity, {
              isResolvedFromMasterCity: Boolean(fallbackCity),
              isFallbackLocation: true,
              isOutsideSupportedRegion: false,
              fallbackReason:
                "Detected location is inside Saudi Arabia, but the city could not be matched with BaytBuddy supported cities.",
            });
          }
        } else {
          normalizedLocation = buildNormalizedLocation(apiData, fallbackCity, {
            isResolvedFromMasterCity: Boolean(fallbackCity),
            isFallbackLocation: true,
            isOutsideSupportedRegion: true,
            fallbackReason:
              "Detected location is outside Saudi Arabia. Riyadh is selected as the default browsing city.",
          });
        }

        if (requestRevision === locationRevision.current) updateLocation(normalizedLocation);
        return normalizedLocation;
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch your location.";
        setLocationError(message);
        throw error;
      } finally {
        setIsDetectingLocation(false);
      }
    },
    [cities, loadCities, updateLocation]
  );

  const detectLocation = useCallback(async (citiesListParam = null) => {
    const requestRevision = locationRevision.current;
    setIsDetectingLocation(true);
    setLocationError("");
    try {
      const position = await getBrowserPosition();
      // A manual selection made while permission was pending takes precedence.
      if (requestRevision !== locationRevision.current) return getStoredLocation();
      return await fetchLocationFromCoordinates(position.coords.latitude, position.coords.longitude, citiesListParam);
    } catch (error) {
      const message = { 1: "Location permission was denied.", 2: "Location information is unavailable.", 3: "Location request timed out." }[error?.code]
        || error?.message || "Unable to detect your location.";
      if (requestRevision === locationRevision.current) setLocationError(message);
      throw new Error(message);
    } finally {
      setIsDetectingLocation(false);
    }
  }, [fetchLocationFromCoordinates]);

  useEffect(() => {
    loadCities().catch(() => {
      // silently fail so app UI does not break
    });
  }, [loadCities]);

  useEffect(() => {
    if (cities.length === 0 || automaticDetectionAttempted.current) return;
    automaticDetectionAttempted.current = true;
    const canonical = canonicalLocation(location, cities);
    const hasSupportedCity = cities.some(city => String(city.id) === String(canonical?.cityId));
    if (hasSupportedCity) {
      updateLocation(canonical);
    } else {
      updateLocation(buildNormalizedLocation({}, findFallbackCityFromMasterCities(cities), {
        isResolvedFromMasterCity: true,
        isFallbackLocation: true,
      }));
    }
    if (!hasSupportedCity || isLocationStale(location)) {
      detectLocation(cities).catch(() => {
        // Keep the saved/default city; a failed attempt must not start a retry loop.
      });
    }
  }, [cities, location, detectLocation, updateLocation]);

  const value = useMemo(
    () => ({
      location,
      cities,
      setLocation: updateLocation,
      clearLocation,
      reloadCities: loadCities,
      isLoadingCities,
      isDetectingLocation,
      locationError,
      detectLocation,
      fetchLocationFromCoordinates,
      detectedCity: cities.length ? canonicalLocation(location, cities)?.city || "" : "",
      detectedCityAr: location?.cityAr || "",
      detectedCityId: cities.length ? canonicalLocation(location, cities)?.cityId || "" : "",
      detectedStateId: location?.stateId || "",
      detectedCountry: location?.country || "",
      detectedAt: location?.detectedAt || null,
      detectedSourceState: location?.sourceState || "",
      detectedSourceArea: location?.sourceArea || "",
      detectedSourceDistrict: location?.sourceDistrict || "",
      detectedSourceFormattedAddress:
        location?.sourceFormattedAddress || "",
      isResolvedFromMasterCity: Boolean(location?.isResolvedFromMasterCity),
      isFallbackLocation: Boolean(location?.isFallbackLocation),
      isOutsideSupportedRegion: Boolean(location?.isOutsideSupportedRegion),
      fallbackReason: location?.fallbackReason || "",
      isLocationStale: isLocationStale(location),
    }),
    [
      location,
      cities,
      updateLocation,
      clearLocation,
      loadCities,
      isLoadingCities,
      isDetectingLocation,
      locationError,
      detectLocation,
      fetchLocationFromCoordinates,
    ]
  );

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);

  if (!context) {
    throw new Error("useLocation must be used within a LocationProvider");
  }

  return context;
}
